import React, { useRef } from 'react';
import 'twin.macro';
import { v4 as uuid } from 'uuid';
import { SelectCustomField } from 'components/NewCustomFields/SelectCustomField';
import _isArray from 'lodash-es/isArray';
import { CustomFieldDefinition, CustomFieldOption } from 'types/customField';
import tw from 'twin.macro';
import useUpdateElevesCustomFields from 'queries/eleves/customFields/useUpdateElevesCustomFields';
import { ROSelect } from 'containers/Eleves/List/Table/Cells/components/ROSelect';
import { Tag } from 'components/Tag';
import PortalWrapper from 'components/PortalWrapper';
import { autoUpdate, useFloating } from '@floating-ui/react-dom';
import { autoPlacement, inner } from '@floating-ui/react';

interface Props {
  value: string[] | string | undefined;
  customFields: CustomFieldDefinition[];
  onChange: (v: string[] | undefined) => any;
  fieldId: string;
  readonly?: boolean;
}

export function MultiTagCell(props: Props) {
  const { value, customFields, onChange, fieldId, readonly } = props;
  const matchingCustomField = customFields.find(c => c.id === fieldId);
  const updateCustomFields = useUpdateElevesCustomFields();
  const listRef = useRef<Array<HTMLElement | null>>([]);

  const [editing, setEditing] = React.useState<boolean>(false);
  const [selectedOption, setSelectedOption] = React.useState<
    string[] | string | undefined
  >(value);

  const { refs, floatingStyles } = useFloating({
    middleware: [
      autoPlacement({
        crossAxis: true,
        alignment: 'start',
        autoAlignment: false,
        allowedPlacements: ['top-start', 'right-end', 'bottom-end', 'left-end'],
      }),
      inner({
        listRef,
        index: 0,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });
  if (!matchingCustomField) return null;
  const options = matchingCustomField?.options || [];
  const opts = options.filter(o => {
    if (_isArray(selectedOption)) {
      return selectedOption.includes(o.id);
    }
    return o.id === selectedOption;
  });

  if (readonly) {
    if (opts.length < 1) return null;
    return (
      <div tw="flex flex-wrap gap-x-1">
        {opts.map(opt => (
          <Tag key={opt.id} color={opt?.color || 'gris-moderate'}>
            {opt?.value}
          </Tag>
        ))}
      </div>
    );
  }
  return (
    <div tw="max-w-[18rem]" ref={refs.setReference}>
      <ROSelect onClick={() => setEditing(true)}>
        <div
          css={[tw`flex flex-wrap gap-x-1 gap-y-1`, editing && tw`invisible`]}
        >
          {opts.map(opt => (
            <Tag key={opt.id} color={opt?.color || 'gris-moderate'}>
              <span tw="truncate">{opt?.value}</span>
            </Tag>
          ))}
        </div>
      </ROSelect>
      {editing && (
        <PortalWrapper portal>
          <div ref={refs.setFloating} style={floatingStyles}>
            <div
              ref={node => {
                listRef.current[0] = node;
              }}
            >
              <SelectCustomField
                tw="border-opacity-0 hover:border-opacity-100 px-1 max-w-[18rem]"
                name={matchingCustomField?.name}
                onClickOutside={() => {
                  setTimeout(() => setEditing(false), 100);
                }}
                openOnMount
                allowMultipleValue
                options={(matchingCustomField?.options || []).map(c => ({
                  id: c.id,
                  value: c.value,
                  color: c.color,
                }))}
                onCreateOption={({ optionValue, color }) => {
                  const optionId = uuid();
                  const newOption: CustomFieldOption = {
                    id: optionId,
                    value: optionValue,
                    color: color || 'framboise-intense',
                    position: 1,
                  };
                  const newValue = _isArray(value)
                    ? [...value, optionId]
                    : [value as string, optionId];
                  const newSelection = _isArray(selectedOption)
                    ? [...selectedOption, optionId]
                    : [selectedOption as string, optionId];
                  setSelectedOption(newSelection);
                  onChange(newValue);
                  return updateCustomFields({
                    ...matchingCustomField,
                    options: [
                      ...(matchingCustomField.options || []),
                      newOption,
                    ],
                  });
                }}
                onRemoveOption={id =>
                  updateCustomFields({
                    ...matchingCustomField,
                    options: matchingCustomField.options.filter(
                      o => o.id !== id,
                    ),
                  })
                }
                onUpdateOption={v =>
                  updateCustomFields({
                    ...matchingCustomField,
                    options: matchingCustomField.options.map(o =>
                      o.id === v.id
                        ? {
                            value: v.value,
                            id: v.id,
                            color: v.color,
                            position: o.position,
                          }
                        : o,
                    ),
                  })
                }
                value={
                  (_isArray(selectedOption)
                    ? selectedOption
                    : [selectedOption]) as string[]
                }
                onChange={v => {
                  setSelectedOption(v);
                  onChange(v);
                }}
              />
            </div>
          </div>
        </PortalWrapper>
      )}
    </div>
  );
}
